import type { ComponentType, FC } from "react";
import { useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import useLatestValue from "app/custom-hooks/useLatestValue";
import { approveContactsTermsThunk } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/effects";
import { selectIsContactsPendingApproval } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import ApproveTermsModal from "../components/ContactsUpsellOverlay/ApproveTermsModal/ApproveTermsModal";
import useDeferredPromise from "../hooks/useDeferredPromise";
import trackingService from "../services/tracking/trackingService";

enum ModalOpenState {
    Opening,
    Opened,
    Closing,
    Closed,
}

export interface IApproveTermsGuardHOCProps {
    onApprove?: () => Promise<unknown> | undefined;
    onClickBeforeApprove?: () => void;
}

interface IComponentProps {
    onClick?: (...args: unknown[]) => void;
}

type OnClickType<C extends IComponentProps = IComponentProps> = C["onClick"];

type OnClickArgs<C extends IComponentProps = IComponentProps> =
    OnClickType<C> extends (...args: infer A) => unknown ? A : never;

function withApproveTermsGuardButton<C extends IComponentProps = IComponentProps>(
    Component: FC<C>,
): ComponentType<C & IApproveTermsGuardHOCProps> {
    return function WrappedWithApproveTermsGuard({
        onClick,
        onApprove,
        onClickBeforeApprove,
        ...otherComponentsProps
    }: C & IApproveTermsGuardHOCProps) {
        const dispatch = useDispatch();
        const deferredPromise = useDeferredPromise();

        const [isLoading, setIsLoading] = useState(false);
        const [modalOpenState, setModalOpenState] = useState<ModalOpenState>(ModalOpenState.Closed);
        const isContactsPendingApproval = useSelector(selectIsContactsPendingApproval);

        const isModalOpen = modalOpenState < ModalOpenState.Closing || isLoading;
        const isModalShown = modalOpenState < ModalOpenState.Closed || isLoading;

        const handleModalCloseRef = useLatestValue(() => {
            setIsLoading(false);
            setModalOpenState(ModalOpenState.Closing);
        });

        const handleClick = (...args: OnClickArgs<C>): void => {
            if (onClickBeforeApprove) {
                onClickBeforeApprove();
            }

            if (!onClick) {
                return;
            }

            if (isContactsPendingApproval) {
                setModalOpenState(ModalOpenState.Opened);

                deferredPromise.createPromise().then(() => {
                    onClick(...args);
                });
            } else {
                return onClick(...args);
            }
        };

        const handleCancel = () => {
            setModalOpenState(ModalOpenState.Closing);

            deferredPromise.reject();
        };

        const handleModalAfterClose = () => {
            setModalOpenState(ModalOpenState.Closed);

            deferredPromise.resolve();
        };

        const handleApprove = () => {
            setIsLoading(true);

            trackingService.trackApproveTerms();
            dispatch(approveContactsTermsThunk());
        };

        useDidUpdate(() => {
            if (isContactsPendingApproval || !isModalOpen) {
                return;
            }

            const approveResult = onApprove?.();

            if (is.isPromise(approveResult)) {
                approveResult.finally(() => {
                    handleModalCloseRef.current();
                });
            } else {
                handleModalCloseRef.current();
            }
        }, [isContactsPendingApproval]);

        const componentsProps = {
            ...otherComponentsProps,
            onClick: handleClick,
        } as unknown as C;

        return (
            <>
                {isModalShown ? (
                    <ApproveTermsModal
                        isOpen={isModalOpen}
                        isLoading={isLoading}
                        onCancel={handleCancel}
                        onApprove={handleApprove}
                        onAfterClose={handleModalAfterClose}
                    />
                ) : null}
                <Component {...componentsProps} />
            </>
        );
    };
}

export default withApproveTermsGuardButton;
